import { FormLabel, Skeleton, Stack, Typography } from '@mui/material'
import { C1Chip } from 'c1ui/components/C1Chip'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { MinimalAPIError } from 'components/core/errors/api'
import { C1Icon } from 'components/core/icons/C1Icon'
import { RelativeTimestamp } from 'components/core/time/display'
import { UserDisplayNameWithFetch } from 'components/pages/user/user-display-name'
import { TriangleAlert } from 'lucide-react'
import { FC } from 'react'
import { FormattedMessage } from 'react-intl'
import { DelegationType, UserDelegationFormData } from './user-delegate-types'

interface DelegateDisplayProps {
    formValues: UserDelegationFormData
    isError: boolean,
    isLoading: boolean,
}

export const DelegateDisplay: FC<DelegateDisplayProps> = ({ formValues, isError, isLoading }) => {
    if (isLoading) {
        return <Skeleton variant="text" />
    }
    if (isError) {
        return <MinimalAPIError
            err={<FormattedMessage
                id="delegate_user_get_error"
                defaultMessage="Error getting delegate user"
            />}
        />
    }
    return (
        <>
            <Stack direction="row" spacing={6} margin={2}>
                <Stack spacing={1}>
                    <FormLabel>
                        <FormattedMessage
                            id="out_of_office_delegate_delegated_to"
                            defaultMessage="Delegated to"
                        />
                    </FormLabel>
                    <Typography variant="body1" color="text.secondary">
                        {formValues.delegatedUserId ? (
                            <UserDisplayNameWithFetch
                                id={formValues.delegatedUserId}
                                includeDetails={false}
                                readOnly
                            />
                        ) : (
                            <Typography variant="unconfigured">
                                <FormattedMessage id="delegated_user_empty_state" defaultMessage="None" />
                            </Typography>
                        )}
                    </Typography>
                </Stack>
            </Stack>
            { formValues.delegationType === DelegationType.TEMPORARY && (
                <Stack direction="row" spacing={6} margin={2}>
                    <Stack spacing={1} sx={{ width: '180px' }}>
                        <FormLabel>
                            <FormattedMessage
                                id="out_of_office_delegate_start_date"
                                defaultMessage="Start date"
                            />
                        </FormLabel>
                        {formValues.startAt ? (
                            <RelativeTimestamp date={formValues.startAt} variant="date" />
                        ) : (
                            <C1EmptyIndicator />
                        )}
                    </Stack>
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1}>
                            <FormLabel>
                                <FormattedMessage
                                    id="out_of_office_delegate_end_date"
                                    defaultMessage="End date"
                                />
                            </FormLabel>
                            {formValues.expirationAt && formValues.expirationAt < new Date() && (
                                <C1Chip
                                    color="warning"
                                    size="small"
                                    label={<FormattedMessage
                                        id="out_of_office_delegate_end_date_expired"
                                        defaultMessage="Expired"
                                    />}
                                    icon={<C1Icon icon={TriangleAlert} />}
                                />
                            )}
                        </Stack>
                        {formValues.expirationAt ? (
                            <RelativeTimestamp date={formValues.expirationAt} variant="date" />
                        ) : (
                            <C1EmptyIndicator />
                        )}
                    </Stack>
                </Stack>
            )}
        </>
    )
}
